import { useRef, useState } from "react";

/* Tiny native HTML5 drag-and-drop helper for reordering an array-backed
   list (product lines, fees, credits, etc.). No external dependency.
 *
 * Usage:
 *   var dnd = useDragReorder(items, function(reordered) { setItems(reordered); });
 *   ...
 *   {items.map(function (item, idx) {
 *     return (
 *       <tr key={item.id || idx} {...dnd.rowProps(idx)}>
 *         <td {...dnd.handleProps()}>⠿</td>
 *         ...
 *       </tr>
 *     );
 *   })}
 *
 * The handle cell sets draggable=true; the row receives onDragOver /
 * onDrop. Dropping on row N moves the dragged row to position N
 * (insert-before semantics: drag down moves to "above the target");
 * onReorder is called with the new array. The helper itself never
 * mutates state.
 *
 * Visual hover/drop indicator: rowProps applies a subtle outline on
 * the row currently being hovered while dragging.
 */
export default function useDragReorder(items, onReorder) {
  var dragIdxRef = useRef(-1);
  var [overIdx, setOverIdx] = useState(-1);

  function reorder(from, to) {
    if (from === to || from < 0 || to < 0) return;
    if (!Array.isArray(items)) return;
    var next = items.slice();
    var moved = next.splice(from, 1)[0];
    next.splice(to, 0, moved);
    onReorder(next);
  }

  function handleProps() {
    return {
      draggable: true,
      onDragStart: function (e) {
        /* dataTransfer is required by Firefox for the drag to begin.
           We DON'T stopPropagation here — the dragstart event needs to
           bubble up to the parent <tr>'s onDragStart so that the row
           can record its own index in dragIdxRef (the row is the only
           thing that knows its idx via closure). */
        try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", "row"); } catch (err) {}
      },
      style: { cursor: "grab", userSelect: "none" },
    };
  }

  function rowProps(idx) {
    return {
      onDragStart: function (e) {
        /* Fired when the handle inside this row starts dragging. We
           record the source index here on the row so onDrop can read it. */
        dragIdxRef.current = idx;
        try { e.dataTransfer.effectAllowed = "move"; } catch (err) {}
      },
      onDragOver: function (e) {
        if (dragIdxRef.current < 0) return;
        e.preventDefault();
        try { e.dataTransfer.dropEffect = "move"; } catch (err) {}
        if (overIdx !== idx) setOverIdx(idx);
      },
      onDragLeave: function () {
        /* Per-row leave: only clear if we were the highlighted target. */
        if (overIdx === idx) setOverIdx(-1);
      },
      onDrop: function (e) {
        e.preventDefault();
        var from = dragIdxRef.current;
        dragIdxRef.current = -1;
        setOverIdx(-1);
        if (from < 0 || from === idx) return;
        reorder(from, idx);
      },
      onDragEnd: function () {
        dragIdxRef.current = -1;
        setOverIdx(-1);
      },
      style: overIdx === idx ? { outline: "2px dashed var(--color-accent)", outlineOffset: -2 } : undefined,
    };
  }

  return { rowProps: rowProps, handleProps: handleProps };
}

/* Standard handle glyph + style — use as a single cell at the start of
   each row for visual consistency across modules. */
export function DragHandle(props) {
  var hp = props.handleProps || {};
  return (
    <span
      {...hp}
      title={props.title || "Drag to reorder"}
      style={{
        ...(hp.style || {}),
        display: "inline-block",
        padding: "0 4px",
        color: "var(--color-text-muted)",
        fontSize: 14,
        lineHeight: 1,
        opacity: 0.55,
      }}
    >
      {"⠿"}
    </span>
  );
}
